'use client';
import * as React from 'react';

import {
  Area,
  AreaChart as LibAreaChart,
  CartesianGrid,
  Label,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useMediaQuery } from 'usehooks-ts';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { Spinner } from '@uikit/components/Spinner';

import { formatReward } from '../../../../../utils';
import { Typography } from '../../../Typography';
import { ChartTooltip } from '../../appearance/ChartTooltip/ChartTooltip';
import { ChartXAxisTick } from '../../appearance/ChartXAxisTick/ChartXAxisTick';
import { ChartYAxisLabel } from '../../appearance/ChartYAxisLabel/ChartYAxisLabel';
import { ChartYAxisTick } from '../../appearance/ChartYAxisTick/ChartYAxisTick';

interface DataItem {
  timestamp: number;
  price: number;
}

type LineChartProps = {
  data: DataItem[];
  xFormatter?: (value: string) => string;
  isLoading?: boolean;
};

export const AreaChart = ({ data, xFormatter, isLoading = false }: LineChartProps) => {
  const isMobile = useMediaQuery('(max-width: 768px)');

  if (isLoading || !data.length) {
    return (
      <div className="flex flex-col gap-2 w-full h-full justify-center items-center">
        {isLoading ? (
          <>
            <Spinner size="sm" color="disabled" className="size-6" />
            <Typography variant="caption-s" color="tertiary">
              Loading Data
            </Typography>
          </>
        ) : (
          <Typography variant="caption-s" color="tertiary">
            No Data
          </Typography>
        )}
      </div>
    );
  }

  const currentPrice = formatReward(data[data.length - 1].price);

  return (
    <ResponsiveContainer>
      <LibAreaChart
        data={data}
        margin={{ top: 20, right: 0, left: 0, bottom: 20 }}
        className="w-full"
      >
        <defs>
          <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
            <stop
              offset="50%"
              stopColor="rgb(var(--colors-base-border-blue-dark))"
              stopOpacity={0.15}
            />
            <stop
              offset="100%"
              stopColor="rgb(var(--colors-base-bg-transparent))"
              stopOpacity="0"
            />
          </linearGradient>
        </defs>
        <XAxis
          dataKey="timestamp"
          tick={(props) => <ChartXAxisTick {...props} tickFormatter={xFormatter} />}
          axisLine={{ stroke: 'rgb(var(--colors-base-border-medium))', strokeDasharray: '2 2' }}
          tickLine={false}
          textAnchor="start"
          interval="preserveStart"
        />

        {!isMobile && (
          <YAxis
            orientation="right"
            axisLine={false}
            tickLine={false}
            textAnchor="start"
            domain={['dataMin', 'dataMax']}
            tick={(props) => (
              <ChartYAxisTick
                {...props}
                textAnchor="start"
                tickFormatter={(value: number) => formatReward(value)}
              />
            )}
          />
        )}

        <ReferenceLine y={currentPrice} stroke="transparent">
          <Label content={ChartYAxisLabel} value={currentPrice} position="right" />
        </ReferenceLine>

        <CartesianGrid
          stroke="rgb(var(--colors-base-border-medium))"
          strokeDasharray="2 2"
          vertical={false}
        />
        <Tooltip
          cursor={{
            stroke: 'rgb(var(--colors-base-border-dark))',
            strokeWidth: 2,
          }}
          content={<ChartTooltip />}
        />
        <Area
          type="monotone"
          dataKey="price"
          stroke="rgb(var(--colors-base-text-blue))"
          fillOpacity={1}
          fill="url(#colorUv)"
          activeDot={{
            r: 4,
            stroke: 'rgb(var(--colors-base-text-blue))',
            fill: 'rgb(var(--colors-base-bg))',
            strokeWidth: 3,
          }}
        />
      </LibAreaChart>
    </ResponsiveContainer>
  );
};
